import { WithChildren } from '@config';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import React from 'react';
import { IntlProvider } from 'react-intl';
import { ToastContainer } from 'react-toastify';
import { flattenMessages } from 'services/i18n/intl';
import enMessages from 'translations/en.json';

import 'assets/reset.css';
import 'global.scss';
import 'react-toastify/dist/ReactToastify.css';
import 'react-datepicker/dist/react-datepicker.css';

/* Language polyfills needed for IE11, Edge, Safari 12 & 13 support
https://github.com/formatjs/react-intl/blob/master/docs/Upgrade-Guide.md#migrate-to-using-native-intl-apis
*/
import '@formatjs/intl-pluralrules/polyfill';

import '@formatjs/intl-relativetimeformat/polyfill';
/* End of language polyfills */

import styles from './Root.module.scss';

const locales = {
  en: flattenMessages(enMessages),
};

const PROD_LZ_POPUP_MESSAGE =
  'Dear users, We would like to inform you that from 4th May 2026 a new URL will be available for Well Guardian. Please stay tune';
const OLD_PROD_LZ_HOSTNAME = 'wellguardian.totalenergies.com';
const NEW_PROD_LZ_BASE_URL = 'https://www.wellguardian.iasp.tgscloud.net';
const OLD_QA_LZ_HOSTNAME = 'qa.wellguardian.totalenergies.com';
const NEW_QA_LZ_BASE_URL = 'https://qa.wellguardian.iasp.tgscloud.net';
const PROD_LZ_REDIRECT_START_AT_MS = new Date('2026-05-04T00:00:00').getTime();
const PROD_LZ_POPUP_DISMISSED_UNTIL_KEY = 'wg_prod_lz_popup_dismissed_until';
const PROD_LZ_POPUP_RESHOW_DELAY_MS = 1 * 60 * 60 * 1000;
const PROD_LZ_POPUP_START_AT_MS = new Date('2026-04-27T00:00:00').getTime();
const PROD_LZ_POPUP_END_AT_MS = new Date('2026-05-02T00:00:00').getTime();

const getRedirectBaseUrl = (): string | null => {
  if (window.location.hostname === OLD_PROD_LZ_HOSTNAME) {
    return NEW_PROD_LZ_BASE_URL;
  }

  if (window.location.hostname === OLD_QA_LZ_HOSTNAME) {
    return NEW_QA_LZ_BASE_URL;
  }

  return null;
};

const getRedirectUrl = (): string | null => {
  if (Date.now() < PROD_LZ_REDIRECT_START_AT_MS) {
    return null;
  }

  const redirectBaseUrl = getRedirectBaseUrl();
  if (!redirectBaseUrl) {
    return null;
  }

  return `${redirectBaseUrl}${window.location.pathname}${window.location.search}${window.location.hash}`;
};

const getIsWithinProdLzPopupWindow = (): boolean => {
  const now = Date.now();
  return now >= PROD_LZ_POPUP_START_AT_MS && now < PROD_LZ_POPUP_END_AT_MS;
};

const getProdLzPopupDismissedUntil = (): number | null => {
  try {
    const dismissedUntilRaw = localStorage.getItem(PROD_LZ_POPUP_DISMISSED_UNTIL_KEY);
    if (!dismissedUntilRaw) {
      return null;
    }

    const dismissedUntil = Number(dismissedUntilRaw);
    if (!Number.isFinite(dismissedUntil)) {
      return null;
    }

    const maxDismissedUntil = Date.now() + PROD_LZ_POPUP_RESHOW_DELAY_MS;
    if (dismissedUntil > maxDismissedUntil) {
      localStorage.setItem(PROD_LZ_POPUP_DISMISSED_UNTIL_KEY, String(maxDismissedUntil));
      return maxDismissedUntil;
    }

    return dismissedUntil;
  } catch {
    return null;
  }
};

const getShouldShowProdLzPopup = (): boolean => {
  if (!getIsWithinProdLzPopupWindow()) {
    return false;
  }

  const dismissedUntil = getProdLzPopupDismissedUntil();
  if (dismissedUntil === null) {
    return true;
  }

  return Date.now() >= dismissedUntil;
};

const Root: React.FC<WithChildren> = ({ children }) => {
  const redirectUrl = getRedirectUrl();
  const shouldRedirectToNewLz = Boolean(redirectUrl);
  const [showProdLzPopup, setShowProdLzPopup] = React.useState(getShouldShowProdLzPopup);
  const shouldShowProdLzPopup = showProdLzPopup;

  React.useEffect(() => {
    if (!redirectUrl) {
      return;
    }

    window.location.replace(redirectUrl);
  }, [redirectUrl]);

  React.useEffect(() => {
    if (showProdLzPopup || !getIsWithinProdLzPopupWindow()) {
      return undefined;
    }

    const dismissedUntil = getProdLzPopupDismissedUntil();
    if (dismissedUntil === null) {
      return undefined;
    }

    const remainingDelay = dismissedUntil - Date.now();
    if (remainingDelay <= 0) {
      setShowProdLzPopup(true);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      if (getShouldShowProdLzPopup()) {
        setShowProdLzPopup(true);
      }
    }, remainingDelay);

    return () => window.clearTimeout(timeoutId);
  }, [showProdLzPopup]);

  const closeProdLzPopup = React.useCallback(() => {
    setShowProdLzPopup(false);
    try {
      localStorage.setItem(
        PROD_LZ_POPUP_DISMISSED_UNTIL_KEY,
        String(Date.now() + PROD_LZ_POPUP_RESHOW_DELAY_MS),
      );
    } catch {
      // no-op: fallback to state-only behavior if storage is unavailable
    }
  }, []);

  if (shouldRedirectToNewLz) {
    return null;
  }

  return (
    <IntlProvider locale="en" messages={locales.en}>
      <div className={styles.rootContainer}>
        <Dialog
          open={shouldShowProdLzPopup}
          onClose={closeProdLzPopup}
          aria-labelledby="prod-lz-information-title"
          aria-describedby="prod-lz-information-description"
          maxWidth="md"
          scroll="body"
          classes={{ paperWidthMd: styles.paperWidthMd, root: styles.root }}
        >
          <DialogTitle
            id="prod-lz-information-title"
            component="div"
            classes={{ root: styles.titleDialog }}
          >
            <Typography variant="h6">Information</Typography>
          </DialogTitle>
          <DialogContent id="prod-lz-information-description" classes={{ root: styles.contentRoot }}>
            <div className={styles.modalText}>{PROD_LZ_POPUP_MESSAGE}</div>
          </DialogContent>
          <DialogActions classes={{ root: styles.actionButtons }}>
            <Button className={styles.confirmButton} onClick={closeProdLzPopup} autoFocus>
              Close
            </Button>
          </DialogActions>
        </Dialog>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable={false}
          pauseOnHover
        />
        {children}
      </div>
    </IntlProvider>
  );
};

export default Root;
